import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, computed, inject, input, OnDestroy, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { AvailableTemplatesService } from '../available-templates.service';
import { AlertSettingsVariable } from 'src/app/hickory-api';
import { PlaceholderUtilities } from '../placeholder-utilities';
import { AlertBuilderService } from 'src/app/alert/shared/alert-builder';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-templated-input-preview',
  templateUrl: './templated-input-preview.component.html',
  styleUrl: './templated-input-preview.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, MatCheckboxModule],
})
export class TemplatedInputPreviewComponent implements AfterViewInit, OnDestroy {
  private readonly placeholderService = inject(AvailableTemplatesService);
  private readonly alertBuilder = inject(AlertBuilderService);
  private readonly disposables = new Subscription();

  readonly text = input<string>('');
  readonly warnOnContactPlaceholders = input<boolean>(false);

  readonly displayChips = signal(true);
  readonly useSample = signal(false);
  readonly allVariables = signal<AlertSettingsVariable[]>([]);

  readonly textHasPlaceholders = computed(() => {
    const textValue = this.text();
    if (!textValue) {
      return false;
    }
    return PlaceholderUtilities.extractPlaceholders(textValue).length != 0;
  });

  readonly previewHtml = computed(() => {
    const textValue = this.text();
    const variables = this.allVariables();
    const showChips = this.displayChips();
    const showSample = this.useSample();
    const warnContact = this.warnOnContactPlaceholders();

    return PlaceholderUtilities.replacePlaceholders(textValue, (placeholderName) => {
      const foundVar = variables.find((item) => placeholderName === item.placeHolder);

      if (!foundVar) {
        return placeholderName;
      }

      let chipStyle = '';

      if (showChips) {
        chipStyle = 'chip-style';

        if (warnContact && (foundVar.source === 'Contact' || foundVar.source === 'CSV')) {
          chipStyle = 'chip-style-warning';
        }
      }

      return `<span class="${chipStyle}">${showSample ? foundVar.sampleValue : foundVar.friendlyName}</span>`;
    });
  });

  ngAfterViewInit(): void {
    this.disposables.add(
      this.placeholderService.getAvailableTemplates(this.alertBuilder?.alertTemplate?.id).subscribe((result) => {
        this.allVariables.set(result.items);
      }),
    );
  }

  ngOnDestroy(): void {
    this.disposables.unsubscribe();
  }
}
